import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, GlobalStyles, Typography, Button, Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import { getTheme } from './themes/themes';
import { RouterProvider } from 'react-router-dom';
import { router } from './Routes';
import { AlertProvider, DatastoreProvider, IsOnlineProvider, LoadingProvider, UserLoginProvider } from './hooks';
import { ServiceWorkerUpdate, SyncSuccessToast, SyncBlocker, VirtualPrintToast } from './components';
import { UserDetailsProvider } from './contexts/UserDetailsContext';
import { useMemo, useState } from 'react';
import { ThemeModeContext } from './contexts/ThemeModeContext';

function AppContent() {
  return (
    <>
      <SyncSuccessToast />
      <VirtualPrintToast />
      <ServiceWorkerUpdate>
        <LoadingProvider>
          <UserLoginProvider>
            <AlertProvider>
              <DatastoreProvider>
                <SyncBlocker>
                  <UserDetailsProvider>
                    <RouterProvider router={router} fallbackElement={<CircularProgress />} />
                  </UserDetailsProvider>
                </SyncBlocker>
              </DatastoreProvider>
            </AlertProvider>
          </UserLoginProvider>
        </LoadingProvider>
      </ServiceWorkerUpdate>
    </>
  );
}

function App() {
  const [mode, setMode] = useState<'light' | 'dark'>(
    () => (localStorage.getItem('themeMode') as 'light' | 'dark') || 'light'
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode((m) => {
      const next = m === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', next);
      return next;
    });
  };

  return (
    <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            body: {
              overscrollBehavior: 'none'
            },
            html: {
              overscrollBehavior: 'none'
            }
          }}
        />
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                Something went wrong
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                An unexpected error occurred. The error has been reported.
              </Typography>
              <Button variant="contained" onClick={resetError}>
                Try Again
              </Button>
            </Box>
          )}>
          <IsOnlineProvider>
            <AppContent />
          </IsOnlineProvider>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export default App;
